import { createContext, useContext } from 'react';

/**
 * Simplified PlaybackContext for video viewport coordination.
 *
 * This context provides signal-based coordination for video viewports:
 * - Pause all videos when measurement tools are activated
 * - Toggle all videos when spacebar is pressed
 *
 * Video playback is handled by native <video> elements, not by this context.
 * Each video viewport subscribes to these signals and manages its own state.
 */
export interface PlaybackContextValue {
  // Subscribe to pause signal (for measurement tool activation)
  // Returns unsubscribe function - MUST be called on cleanup
  subscribeToVideoPause: (callback: () => void) => () => void;

  // Subscribe to toggle signal (for spacebar hotkey)
  // Returns unsubscribe function - MUST be called on cleanup
  subscribeToVideoToggle: (callback: () => void) => () => void;

  // Pause all video viewports
  pauseAll: () => void;

  // Toggle play/pause for all video viewports
  togglePlayPauseAll: () => void;
}

export const PlaybackContext = createContext<PlaybackContextValue | null>(null);

export function usePlaybackContext(): PlaybackContextValue {
  const context = useContext(PlaybackContext);
  if (!context) {
    throw new Error(
      'usePlaybackContext must be used within a PlaybackContext.Provider'
    );
  }
  return context;
}
